// src/pages/CreateAuction.tsx
import React, { useEffect, useState, useRef } from "react";
import { Modal, Box, Typography, Button } from "@mui/material";
import { AuctionForm } from "../components/exchange/auction";
import type { AuctionFormValues } from "../components/exchange/auction/AuctionForm";
import auctionService from "../services/auctionService";
import { useNavigate } from "react-router-dom";

const SESSION_CHECK_INTERVAL = 2 * 60 * 1000; // 2 minutes

const initialValues: AuctionFormValues = {
  title: "",
  description: "",
  starting_price: 0,
  min_increment: 1,
  end_time: null,
  category: "Other",
  quantity: 1,
  images: [],
};

const CreateAuction: React.FC = () => {
  const [sessionExpired, setSessionExpired] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const navigate = useNavigate();

  const handleSubmit = async (values: AuctionFormValues) => {
    const formData = new FormData();
    formData.append("title", values.title);
    formData.append("description", values.description);
    formData.append("starting_price", String(values.starting_price));
    formData.append("min_increment", String(values.min_increment));
    formData.append(
      "end_time",
      values.end_time ? values.end_time.toISOString() : ""
    );
    formData.append("category", values.category);
    formData.append("quantity", String(values.quantity || 1));
    formData.append("images", JSON.stringify(values.images));

    await auctionService.createItem(formData);
    navigate("/my-pending-auctions");
  };

  useEffect(() => {
    intervalRef.current = setInterval(async () => {
      try {
        await auctionService.verifySession();
      } catch {
        setSessionExpired(true);
        if (intervalRef.current) clearInterval(intervalRef.current);
      }
    }, SESSION_CHECK_INTERVAL);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  const handleLoginRedirect = () => {
    setSessionExpired(false);
    navigate("/login");
  };

  return (
    <>
      <AuctionForm initialValues={initialValues} onSubmit={handleSubmit} />
      <Modal open={sessionExpired} onClose={handleLoginRedirect}>
        <Box
          sx={{
            p: 4,
            bgcolor: "background.paper",
            m: "auto",
            mt: 10,
            borderRadius: 2,
            maxWidth: 400,
          }}
        >
          <Typography variant="h6" gutterBottom>
            Session Expired
          </Typography>
          <Typography gutterBottom>
            Your session has expired. Please log in again to continue and avoid
            losing your work.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={handleLoginRedirect}
          >
            Log In
          </Button>
        </Box>
      </Modal>
    </>
  );
};

export default CreateAuction;
